// 总包管理
import { useCallback, useMemo, useState } from "react";
import { App, Button, Checkbox, ConfigProvider, Divider, Form, FormProps, Input, InputNumber, Popover, Select, Space, Table, TableProps, Tag } from "antd";
import { CheckboxGroupProps } from "antd/es/checkbox";
import { CheckOutlined, CloseOutlined, DollarCircleFilled, EditOutlined, ExportOutlined, FilterFilled } from "@ant-design/icons";
import classNames from "classnames";
import Decimal from "decimal.js";
import { Alignment, Borders, Fill, Workbook } from "exceljs";
import { saveAs } from 'file-saver';
import { useRequest } from "ahooks";
import { ProjectHalSource } from "@/halSources";
import { getProjectCosts, updateProjectCosts } from "@/services";

type CostItem = {
  id: number;
  subitem: string;
  budget: number;
  budgetMoney: number;
  subcontract: number;
};

type CostItemWithDiff = CostItem & {
  contract_diff: number;
  subcontract_diff: number;
  budgetSubcontract_diff: number;
};

type CostField = {
  title: string;
  name: keyof CostItemWithDiff;
  width?: number;
  className?: string;
  visible: boolean;
  diff?: boolean;
  disable?: boolean;
  money?: boolean;
  bgColor?: string;
  editable?: boolean;
};

type AmountConfig = {
  unit: number;
  decimal: number;
  carry: 'round' | 'ceil' | 'floor';
  separation: 0 | 1000 | 10000;
};

type TablePreset = {
  name: string,
  title: string;
  selectedFieldKeys: (keyof CostItemWithDiff)[];
  bgColor?: string;
};

type EditCostItem = Partial<CostItem>;

type Props = {
  project?: ProjectHalSource;
  canEdit?: boolean;
};

const defaultFields: CostField[] = [
  { title: '分项设计专业', name: 'subitem', width: 200, className: 'overflow-hidden text-center', visible: true, disable: true },
  { title: '预算合同额(外切分包价)', name: 'budget', width: 200, className: 'overflow-hidden text-center', visible: true, money: true, bgColor: '#E9F2FF', editable: false },
  { title: '总包合同额(合同报价)', name: 'budgetMoney', width: 200, className: 'overflow-hidden text-center', visible: true, money: true, bgColor: '#F4FAFF', editable: true },
  { title: '预算与总包差额', name: 'contract_diff', width: 200, className: 'overflow-hidden text-center', visible: true, diff: true, money: true },
  { title: '分包合同额(实际执行价)', name: 'subcontract', width: 200, className: 'overflow-hidden text-center', visible: true, money: true, bgColor: '#E2F9EB' },
  { title: '预算与分包差额', name: 'budgetSubcontract_diff', width: 200, className: 'overflow-hidden text-center', visible: true, diff: true, money: true },
  { title: '总包与分包差额', name: 'subcontract_diff', width: 200, className: 'overflow-hidden text-center', visible: true, diff: true, money: true },
];

const amountUnits = [
  { title: '分', value: 1 },
  { title: '元', value: 100 },
  { title: '万元', value: 100 * 10000 },
  { title: '亿元', value: 100 * 10000 * 10000 },
];

const amountCarries = [
  { title: '四舍五入', value: 'round' },
  { title: '向上取整', value: 'ceil' },
  { title: '向下取整', value: 'floor' },
];

const amountSeparations = [
  { title: '不分', value: 0 },
  { title: '千位', value: 1000 },
  { title: '万位', value: 10000 },
];

const tablePresets: TablePreset[] = [
  { name: 'budget', title: '预算', selectedFieldKeys: ['subitem', 'budget'], bgColor: '#1677FF' },
  { name: 'budgetMoney', title: '合同（总包）', selectedFieldKeys: ['subitem', 'budgetMoney'], bgColor: '#52A4F3' },
  { name: 'subcontract', title: '执行（分包）', selectedFieldKeys: ['subitem', 'subcontract'], bgColor: '#29B0AB' },
  { name: 'all', title: '总表（对比）', selectedFieldKeys: defaultFields.map(item => item.name), bgColor: '#FE831E' },
];

/**
 * 分割金额
 */
const splitAmount = (amount: string, separation: AmountConfig['separation']) => {
  if (!separation) return amount;
  const len = String(separation).length - 1;
  const _split = (str: string) => str.replace(new RegExp(`(\\d)(?=(?:\\d{${len}})+$)`, 'g'), '$1,');
  const [int, float] = amount.split('.');
  return [_split(int), float].filter(Boolean).join('.');
};

const decimalAmount = (amount: string, decimal: AmountConfig['decimal'], carry: AmountConfig['carry']) => {
  const _decimal = (int: string, float: string) => {
    if (!float || float.length < decimal) return [int, float];

    const floatStr = float.slice(0, decimal);
    const rest = float.slice(decimal);

    if (carry === 'floor' || !rest) return [int, floatStr];

    let floatNumTemp = Number(floatStr);

    if (carry === 'ceil' || Number(rest[0]) > 4) floatNumTemp += 1;

    const floatStrTemp = String(floatNumTemp).padStart(floatStr.length, '0');

    const eLen = floatStrTemp.length - floatStr.length;

    const eInt = floatStrTemp.slice(0, eLen);

    const isNegative = int[0] === '-';

    const newInt = isNegative ? int.slice(1) : int;

    return [
      [isNegative ? '-' : '', String(Number(newInt) + Number(eInt))].join(''),
      decimal ? floatStrTemp.slice(eLen) : '',
    ];
  };

  const [int, float] = amount.split('.');
  return _decimal(int, float).filter(Boolean).join('.');
};

/**
 * 格式化金额分
 */
const formatPennyAmount = (amount: number, separation: number = 1000) => {
  return String(amount).replace(new RegExp(`((\\d)(?=(?:(\\d{2}$)))|((\\d)(?=(?:(\\d{${String(separation).length - 1}})+\\d{2}$))))`, 'g'), '$1,');
};

/**
 * 格式化金额
 * @param amount 单位分
 */
const formatAmount = (amount: number, config: AmountConfig) => {
  const { unit, decimal, carry, separation } = config;
  if (unit === 1) return formatPennyAmount(amount, 1000);
  const newAmount = new Decimal(amount).div(unit);
  const dcm = decimalAmount(newAmount.toString(), decimal, carry);
  return splitAmount(dcm, separation);
};

const renderDiff = (amount: number, config: AmountConfig) => (
  <span
    className={classNames({
      'text-red-500': amount < 0,
      'text-green-500': amount > 0,
    })}
  >
    {formatAmount(amount, config)}
  </span>
);

export const ProjectCosts: React.FC<Props> = (props) => {
  const { project, canEdit = false } = props;

  const { message } = App.useApp();

  const [fields, setFields] = useState(defaultFields);

  const [data, setData] = useState<CostItem[]>([]);

  const [contractCode, setContractCode] = useState<string>('未知');

  const { refresh } = useRequest(() => getProjectCosts(project!.thEngineCode), {
    manual: false,
    debounceWait: 300,
    refreshDeps: [project],
    onSuccess: (res) => {
      setData(res.map(item => ({
        id: item.generalContractID,
        subitem: item.subitemDesignMajor,
        budget: item.budgetContractAmount || 0,
        budgetMoney: item.budgetMoney || 0,
        subcontract: item.subcontractAmount || 0,
      })));
      if (res.length > 0) setContractCode(res[0].salesContractCode);
    },
  });

  const { run: runUpdate, loading } = useRequest(updateProjectCosts, {
    manual: true,
    debounceWait: 300,
    onSuccess: () => {
      refresh();
      setEditing(false);
    },
    onError: (err) => {
      console.log(err);
      message.error(err.message);
    },
  });

  const [editing, setEditing] = useState(false);

  const [editingCostItems, setEditingCostItems] = useState<Record<CostItem['id'], EditCostItem>>({});

  const [amountConfig, setAmountConfig] = useState<AmountConfig>({
    unit: 100,
    carry: 'round',
    decimal: 2,
    separation: 1000,
  });

  const [amountConfigForm] = Form.useForm<AmountConfig>();

  const dataSource = useMemo(() => data.map(item => {
    // let contract;
    let budget;

    // if (editing) {
    //   const editingCostItem = editingCostItems[item.id];
    //   if (editingCostItem) {
    //     contract = editingCostItem.contract;
    //     budget = editingCostItem.budget;
    //   }
    // }

    // contract ??= (item.contract || 0);
    budget ??= (item.budget || 0);

    return {
      ...item,
      budget,
      // contract,
      contract_diff: new Decimal(budget).sub(item.budgetMoney).toNumber(),
      subcontract_diff: new Decimal(item.budgetMoney).sub(item.subcontract).toNumber(),
      budgetSubcontract_diff: new Decimal(budget).sub(item.subcontract).toNumber(),
    };
  }), [data, editing, editingCostItems]);

  const currentFields = useMemo(() => {
    return fields.filter(item => item.visible);
  }, [fields]);

  const currentAmountConfig = useMemo(() => {
    return editing ? {
      ...amountConfig,
      unit: 1,
    } : amountConfig;
  }, [amountConfig, editing]);

  const renderAmountColumn = useCallback((amount: number, field: CostField) => {
    if (!field.money) throw new Error('字段不是金额类型');
    if (!field.diff) return formatAmount(amount, currentAmountConfig);
    return renderDiff(amount, currentAmountConfig);
  }, [currentAmountConfig]);

  const handleEditingCostItemChange = (key: CostItem['id'], value: EditCostItem) => {
    setEditingCostItems((list) => {
      return {
        ...list,
        [key]: {
          id: key,
          ...list[key],
          ...value,
        },
      };
    });
  };

  const tableColumns: TableProps<CostItemWithDiff>['columns'] = useMemo(() => {
    return currentFields.map(field => ({
      dataIndex: field.name,
      title: field.title,
      width: field.width,
      className: field.className,
      onCell: () => ({
        className: 'p-px',
      }),
      render: (text, record) => (
        <div
          className={classNames("p-3 group-hover:bg-transparent transition-all", {
            'group-hover:scale-150': field.money,
            'group-hover:scale-105': !field.money,
          })}
          style={{ backgroundColor: field.bgColor }}
        >
          {(() => {
            if (field.money) {
              if (!editing || !field.editable) return renderAmountColumn(text, field);
              return (
                <InputNumber
                  addonAfter="分"
                  size="small"
                  min={0}
                  precision={0}
                  step={100}
                  defaultValue={text}
                  formatter={value => value && formatPennyAmount(value)}
                  parser={value => value && value.replace(/\D/g, '')}
                  onChange={value => handleEditingCostItemChange(record.id, { [field.name]: value })}
                />
              );
            }
            if (!editing || !field.editable) return text;
            return (
              <Input
                size="small"
                min={0}
                defaultValue={text}
                onChange={value => handleEditingCostItemChange(record.id, { [field.name]: value })}
              />
            );
          })()}
        </div>
      ),
    }));
  }, [currentFields, editing, renderAmountColumn]);

  const selectedFieldKeys = useMemo(() => {
    return currentFields.map(item => item.name);
  }, [currentFields]);

  const amountUnitLabel = useMemo(() => {
    const u = amountUnits.find(item => item.value === currentAmountConfig.unit);
    return u?.title || '';
  }, [currentAmountConfig]);

  const handleSelectedFieldKeyChange: CheckboxGroupProps['onChange'] = (keys) => {
    setFields((list) => {
      return list.map(item => ({
        ...item,
        visible: keys.includes(item.name),
      }));
    });
  };

  const handleAmountConfigFormChange: FormProps<AmountConfig>['onValuesChange'] = (_, allValues) => {
    setAmountConfig(allValues);
  };

  const handleTablePresetClick = (preset: TablePreset) => {
    setFields((list) => {
      return list.map(item => ({
        ...item,
        visible: preset.selectedFieldKeys.includes(item.name),
      }));
    });
  };

  const handleEditing = () => {
    setEditingCostItems({});
    setEditing(true);
  };

  // const handleEditingConfirm = () => {
  //   runUpdate({
  //     generalContracts: Object.values(editingCostItems).map(item => ({
  //       generalContractID: item.id!,
  //       budgetContractAmount: item.budget!,
  //       totalContractAmount: item.contract!,
  //     })),
  //   });
  // };

  const handleEditingCancel = () => {
    setEditingCostItems({});
    setEditing(false);
  };

  const exportExcel = async () => {
    // 初始化工作表
    const workbook = new Workbook();
    const sheet = workbook.addWorksheet('Sheet1');
    sheet.columns = currentFields.map(field => {
      let title = field.title;
      if (field.money) title += `（${amountUnitLabel}）`;
      return {
        header: title,
        key: field.name,
        width: 30,
      }
    });

    const getDiffFontColor = (diff: number) => {
      return diff > 0 ? 'FF00FF00' : 'FFFF0000';
    };

    // 添加数据
    dataSource.forEach(item => {
      const row = sheet.addRow(item);
      currentFields.forEach(field => {
        const cell = row.getCell(field.name);
        if (field.money) {
          const amount = item[field.name] as number;
          cell.value = formatAmount(amount, amountConfig);
          if (field.diff) {
            cell.font = {
              color: { argb: getDiffFontColor(amount) },
            };
          }
        }
      });
    });

    // 设置样式
    const alignment: Partial<Alignment> = {
      vertical: 'middle',
      horizontal: 'center',
    };
    const fill: Fill = {
      type: 'pattern',
      pattern: 'solid',
    };
    const border: Partial<Borders> = {
      top: { style: 'thin', color: { argb: 'FFF0F0F0' } },
      bottom: { style: 'thin', color: { argb: 'FFF0F0F0' } },
      left: { style: 'thin', color: { argb: 'FFF0F0F0' } },
      right: { style: 'thin', color: { argb: 'FFF0F0F0' } },
    };
    const rowHeight = 30;
    const grayFill: Fill = {
      ...fill,
      fgColor: {
        argb: 'FFFAFAFA',
      },
    };

    currentFields.forEach(field => {
      const col = sheet.getColumn(field.name);
      col.eachCell({ includeEmpty: true }, (cell, rowNumber) => {
        cell.alignment = alignment;
        cell.border = border;
        if (rowNumber === 1) {
          cell.fill = grayFill;
          cell.font = {
            bold: true,
          };
        } else {
          if (field.bgColor) {
            cell.fill = {
              ...fill,
              fgColor: {
                argb: `FF${field.bgColor.slice(1)}`,
              },
            };
          }
        }
      });
    });
    sheet.eachRow((row) => {
      row.height = rowHeight;
    });

    // 添加统计行
    const statistic = currentFields.reduce<Record<string, any>>((obj, field) => {
      if (field.name === 'subitem') obj[field.name] = '合计';
      if (field.name === 'id') obj[field.name] = null;
      if (field.money) {
        obj[field.name] = dataSource.reduce((sum, item) => sum.add(item[field.name]), new Decimal(0)).toNumber();
      }
      return obj;
    }, {});
    const statisticRow = sheet.addRow(statistic);
    statisticRow.height = rowHeight;
    currentFields.forEach((field) => {
      const cell = statisticRow.getCell(field.name);
      cell.alignment = alignment;
      cell.border = border;
      cell.fill = grayFill;
      cell.font = { bold: true };
      if (field.money) {
        const amount = statistic[field.name] as number;
        cell.value = formatAmount(amount, amountConfig);
        if (field.diff) {
          cell.font = {
            bold: true,
            color: { argb: getDiffFontColor(amount) },
          };
        }
      }
    });

    // 导出
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `${project?.name}-成本报表.xlsx`);
  };

  const renderTableSummary: TableProps<CostItemWithDiff>['summary'] = (currentData) => {
    const renderTdContent = (field: CostField) => {
      if (field.name === 'subitem') return '合计';
      if (field.name === 'id') return null;
      const sum = currentData.reduce((sum, item) => sum.add(item[field.name]), new Decimal(0)).toNumber();
      return renderAmountColumn(sum, field);
    };

    return (
      <tr className={classNames('bg-gray-50', { 'group': !editing })}>
        {currentFields.map(field => (
          <td key={field.name} className={classNames(field.className, 'font-bold')} style={{ width: field.width }}>
            <div
              className={classNames("transition-all", {
                'group-hover:scale-150': field.money,
                'group-hover:scale-105': !field.money,
              })}>
              {renderTdContent(field)}
            </div>
          </td>
        ))}
      </tr>
    );
  };

  const amountLike = new Decimal(23803512);

  return (
    <div>
      <div className="flex justify-between items-end">
        <div className="text-sm flex flex-col gap-2">
          <div>
            <span className="text-gray-400">项目名称：</span>
            <span className="select-all">{project?.name}</span>
          </div>
          <div>
            <span className="text-gray-400">合同编号：</span>
            <span className="select-all">{contractCode}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* {canEdit && editing && (
            <>
              <Button
                type="dashed"
                danger
                icon={<CloseOutlined />}
                disabled={loading}
                onClick={handleEditingCancel}
              >
                取消
              </Button>
              <Button
                type="primary"
                icon={<CheckOutlined />}
                loading={loading}
                onClick={handleEditingConfirm}
              >
                完成
              </Button>
            </>
          )}
          {canEdit && !editing && (
            <Button
              type="default"
              icon={<EditOutlined />}
              disabled={editing || dataSource.length === 0}
              onClick={handleEditing}
            >
              编辑
            </Button>
          )} */}
          <Button
            type="default"
            icon={<ExportOutlined />}
            disabled={editing || dataSource.length === 0}
            onClick={exportExcel}
          >
            导出表格
          </Button>
        </div>
      </div>
      <Divider className="my-4" />
      <div className="flex justify-between my-4">
        <div>
          <Space>
            {tablePresets.map(item => (
              <ConfigProvider
                key={item.name}
                theme={{
                  token: {
                    colorPrimary: item.bgColor,
                  }
                }}
              >
                <Button
                  type="primary"
                  disabled={editing}
                  onClick={() => handleTablePresetClick(item)}
                >
                  {item.title}
                </Button>
              </ConfigProvider>
            ))}
          </Space>
        </div>
        <div className="flex items-center gap-2">
          <Tag color="cyan-inverse" className="mr-0">
            {amountUnitLabel}
          </Tag>
          <Popover
            trigger="click"
            content={(
              <Form
                form={amountConfigForm}
                layout="horizontal"
                className="w-40"
                initialValues={amountConfig}
                onValuesChange={handleAmountConfigFormChange}
              >
                <Form.Item label="单位" name="unit">
                  <Select
                    className="w-full"
                    options={amountUnits.map(item => ({
                      label: item.title,
                      value: item.value,
                    }))}
                  />
                </Form.Item>
                <Form.Item label="小数" name="decimal">
                  <InputNumber
                    className="w-full"
                    min={0}
                    max={10}
                    step={1}
                    precision={0}
                  />
                </Form.Item>
                <Form.Item label="进位" name="carry">
                  <Select
                    className="w-full"
                    options={amountCarries.map(item => ({
                      label: item.title,
                      value: item.value,
                    }))}
                  />
                </Form.Item>
                <Form.Item label="分割" name="separation">
                  <Select
                    className="w-full"
                    options={amountSeparations.map(item => ({
                      label: item.title,
                      value: item.value,
                    }))}
                  />
                </Form.Item>
                <div className="text-xs">
                  <div>
                    示例
                  </div>
                  <div className="mt-2">
                    <span className="text-gray-400">
                      原始值（分）：
                    </span>
                    <span className="text-sm">
                      {amountLike.toString()}
                    </span>
                  </div>
                  <div className="mt-1">
                    <span className="text-gray-400">
                      显示值（{amountUnitLabel}）：
                    </span>
                    <span className="text-sm">
                      {formatAmount(amountLike.toNumber(), amountConfig)}
                    </span>
                  </div>
                </div>
              </Form>
            )}
          >
            <Button
              type="default"
              icon={<DollarCircleFilled />}
              disabled={editing}
            >
              金额配置
            </Button>
          </Popover>
          <Popover
            trigger="click"
            content={(
              <Checkbox.Group
                value={selectedFieldKeys}
                onChange={handleSelectedFieldKeyChange}
                className="w-36 flex-col gap-1"
                options={fields.map(field => ({
                  value: field.name,
                  disabled: field.disable,
                  label: field.title,
                }))}
              />
            )}
          >
            <Button
              type="default"
              icon={<FilterFilled />}
              disabled={editing}
            >
              字段配置
            </Button>
          </Popover>
        </div>
      </div>
      <Table
        rowKey="id"
        bordered
        columns={tableColumns}
        dataSource={dataSource}
        onRow={() => ({
          className: !editing ? 'group' : undefined,
        })}
        pagination={false}
        summary={renderTableSummary}
      />
    </div>
  );
};

export default ProjectCosts;
